import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import QrCodeIcon from "@mui/icons-material/QrCode";
import PhotoOutlinedIcon from "@mui/icons-material/PhotoOutlined";
import RedoIcon from "@mui/icons-material/Redo";
import { Divider, Menu, Tooltip, Typography, useTheme } from "@mui/material";
import { GridCellParams } from "@mui/x-data-grid";
import React, { useState } from "react";
import { useModal } from "../modal/context";
import { Styled } from "./styles";
import { TableParams } from "@/types/utils";
import useQueryBrands from "@/hooks/queries/useQueryBrands";

interface Props {
    params: GridCellParams<TableParams>;
}
const ICON_SIZE = 18;

const RowMenu: React.FC<Props> = ({ params }) => {
    const row = params.row;

    const theme = useTheme();
    const onOpen = useModal(state => state.onOpen);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { data: brands } = useQueryBrands();

    const hasMultipleBrands = brands ? brands?.length > 0 : false;
    const isWine = row?.typeName.startsWith("11.02");

    const onOpenMenu = (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
        setAnchorEl(e.currentTarget);
    };
    const onCloseMenu = () => {
        setAnchorEl(null);
    };

    const product = {
        pid: row._id,
        typeName: row.typeName,
        eid: row.eid,
        longTitle: row.longTitle,
        shortTitle: row.shortTitle,
        ...(row.nutrition && { nutrition: row.nutrition }),
    };

    return (
        <Styled.List disablePadding={true}>
            <Item
                Icon={<QrCodeIcon sx={{ height: ICON_SIZE, width: ICON_SIZE }} />}
                message="Download QR code"
                onClick={() => onOpen("qr-code", "Download QR code", product)}
            />
            <Item
                Icon={<VisibilityIcon sx={{ height: ICON_SIZE, width: ICON_SIZE }} />}
                message="View product"
                onClick={() => onOpen("links", "View product", product)}
            />
            <Divider orientation="vertical" variant="middle" flexItem={true} />
            <Item
                Icon={<MoreHorizIcon sx={{ height: ICON_SIZE, width: ICON_SIZE }} />}
                message="More actions"
                onClick={onOpenMenu}
            />
            <Menu
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={onCloseMenu}
            >
                <Styled.List disablePadding={true} sx={{ border: "none", flexDirection: "column" }}>
                    {hasMultipleBrands ? (
                        <Styled.Item
                            onClick={e => {
                                e.stopPropagation(), onOpen("brands", "Move product", product);
                            }}
                            disablePadding={true}
                            sx={{ backgroundColor: theme.palette.secondary.dark }}
                            data-testid="row-menu-move-product"
                        >
                            <RedoIcon sx={{ height: ICON_SIZE, width: ICON_SIZE }} />
                            <Typography variant="body2" marginLeft="10px">
                                Move product
                            </Typography>
                        </Styled.Item>
                    ) : null}
                    {isWine ? (
                        <Styled.Item
                            disablePadding={true}
                            onClick={e => {
                                e.stopPropagation(),
                                    onOpen("cards", "Download product card", product);
                            }}
                            sx={{ backgroundColor: theme.palette.secondary.dark }}
                            data-testid="row-menu-product-card"
                        >
                            <PhotoOutlinedIcon sx={{ height: ICON_SIZE, width: ICON_SIZE }} />
                            <Typography variant="body2" marginLeft="10px">
                                Download product card
                            </Typography>
                        </Styled.Item>
                    ) : null}
                </Styled.List>
            </Menu>
        </Styled.List>
    );
};

interface ItemProps {
    Icon: React.ReactNode;
    onClick: (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
    message: string;
}

const Item: React.FC<ItemProps> = ({ Icon, onClick, message }) => {
    const item = message.includes("QR")
        ? "qr"
        : message.includes("View product")
          ? "view"
          : "actions";
    return (
        <Tooltip title={message}>
            <Styled.Item
                disablePadding={true}
                onClick={e => {
                    e.stopPropagation(), onClick(e);
                }}
                data-testid={`row-menu-${item}`}
            >
                {Icon}
            </Styled.Item>
        </Tooltip>
    );
};

export default RowMenu;
